
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useAuth } from "@/contexts/AuthContext";
import { Separator } from "@/components/ui/separator";

// Define the schema for signin form
const signinFormSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  password: z.string().min(1, { message: "Please enter your password" }),
});

// Create type from the schema
export type SigninFormValues = z.infer<typeof signinFormSchema>;

interface SignInFormProps {
  onSuccess: () => void;
  onSwitchMode: () => void;
  onMagicLinkMode: () => void;
  redirectPath?: string;
}

export function SignInForm({ onSuccess, onSwitchMode, onMagicLinkMode, redirectPath }: SignInFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();

  // Initialize the form
  const form = useForm<SigninFormValues>({
    resolver: zodResolver(signinFormSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const handleSubmit = async (data: SigninFormValues) => {
    setIsLoading(true);
    
    try {
      await login(data.email, data.password);
      
      // Clear form and signal success
      form.reset();
      onSuccess();
      
      // Redirect if needed
      if (redirectPath) {
        window.location.href = redirectPath;
      }
    } catch (error) {
      console.error("Authentication error:", error);
      // The toast is handled in the auth context
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 pt-4">
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Email</FormLabel>
              <FormControl>
                <Input placeholder="your.email@example.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <FormField
          control={form.control}
          name="password"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Password</FormLabel>
              <FormControl>
                <Input type="password" placeholder="••••••••" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        
        <div className="flex flex-col gap-4 pt-2">
          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Signing in...
              </span>
            ) : (
              <>Sign in with Email</>
            )}
          </Button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-background px-2 text-muted-foreground">
                or
              </span>
            </div>
          </div>

          <Button 
            type="button" 
            variant="outline" 
            onClick={onMagicLinkMode} 
            className="w-full"
          >
            Sign in with Magic Link
          </Button>
          
          <Button type="button" variant="link" onClick={onSwitchMode} className="text-sm text-muted-foreground">
            Don't have an account? Sign up
          </Button>
        </div>
      </form>
    </Form>
  );
}
